import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/apiClient";
import { FormulaServiceForm } from "@/components/FormulaServiceForm";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calculator, Link2, Copy, Check, Loader2, FileSignature } from "lucide-react";

function initValue(cfg) {
  return {
    composition_type: "single",
    persone: [{ ...cfg.default_persona, ruolo: "adulto" }],
    ambito: "h24",
    frazionamento: "annuale",
    tutela_legale: "nessuna",
    flessibilita_pct: 0,
  };
}

export default function FormulaServiceCalc() {
  const { isAdmin } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [value, setValue] = useState(null);
  const [calc, setCalc] = useState(null);
  const [link, setLink] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [mine, setMine] = useState([]);
  const [richieste, setRichieste] = useState([]);
  const [adesioneLinks, setAdesioneLinks] = useState({});
  const timer = useRef(null);

  const loadRichieste = () => api.get("/formula-service/richieste/mine").then(({ data }) => setRichieste(data || [])).catch(() => {});

  useEffect(() => {
    api.get("/formula-service/config").then(({ data }) => { setCfg(data); setValue(initValue(data)); });
    api.get("/formula-service/quotes/mine").then(({ data }) => setMine(data || [])).catch(() => {});
    loadRichieste();
  }, []);

  const trasforma = async (rid) => {
    try {
      const { data } = await api.post(`/formula-service/richieste/${rid}/trasforma`, {});
      const url = data.adesione_url?.startsWith("http") ? data.adesione_url : `${window.location.origin}${data.adesione_url}`;
      setAdesioneLinks((s) => ({ ...s, [rid]: url }));
      toast.success(data.already ? "Adesione già generata" : "Adesione a distanza creata: invia il link al cliente per la firma OTP");
      loadRichieste();
    } catch (e) { toast.error(apiError(e)); }
  };

  useEffect(() => {
    if (!value) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const { data } = await api.post("/formula-service/calcola", value);
        setCalc(data);
      } catch (e) { /* ignore transient */ }
    }, 350);
    return () => timer.current && clearTimeout(timer.current);
  }, [value]);

  const genLink = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/formula-service/quotes", value);
      const url = `${window.location.origin}/p/${data.short_id}`;
      setLink(url);
      toast.success("Link cliente generato");
      api.get("/formula-service/quotes/mine").then(({ data }) => setMine(data || [])).catch(() => {});
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const copy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!cfg || !value) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div>;

  return (
    <div className="mx-auto max-w-5xl space-y-6" data-testid="fs-calculator-page">
      <div>
        <h1 className="flex items-center gap-2 font-heading text-3xl font-semibold text-forest">
          <Calculator className="h-7 w-7 text-terracotta" /> Calcolatore Eticapro — Formula Service
        </h1>
        <p className="mt-1 text-muted-foreground">{cfg.product_name} · ed. {cfg.product_version}. Calcolo eseguito lato server.</p>
      </div>

      <FormulaServiceForm cfg={cfg} value={value} onChange={setValue} calc={calc} showFlex={!!isAdmin}>
        <Card className="rounded-xl border-terracotta/30 p-4">
          <p className="mb-2 flex items-center gap-2 text-sm font-semibold text-forest"><Link2 className="h-4 w-4" /> Link per il cliente</p>
          <p className="mb-3 text-xs text-muted-foreground">Genera un link con questo preventivo. Il cliente vedrà il premio finale; lo sconto resta riservato.</p>
          {!link ? (
            <Button onClick={genLink} disabled={saving} data-testid="fs-genera-link" className="w-full gap-2 rounded-full bg-terracotta hover:bg-terracotta/90">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />} Genera link cliente
            </Button>
          ) : (
            <div className="flex items-center gap-2">
              <Input readOnly value={link} data-testid="fs-link-url" className="h-9 text-xs" />
              <Button size="sm" variant="outline" onClick={copy} data-testid="fs-copy-link" className="h-9 shrink-0 gap-1">
                {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
              </Button>
            </div>
          )}
        </Card>
      </FormulaServiceForm>

      {richieste.length > 0 && (
        <Card className="rounded-xl border-border/60 p-4" data-testid="fs-richieste">
          <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-forest"><FileSignature className="h-4 w-4 text-terracotta" /> Richieste ricevute ({richieste.length})</p>
          <div className="space-y-2">
            {richieste.map((r) => (
              <div key={r.id} className="rounded-lg border border-border/60 bg-[#F0F4F1] px-3 py-2.5" data-testid={`fs-richiesta-${r.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="text-sm">
                    <span className="font-medium text-forest">{r.nome} {r.cognome}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{r.email} · {r.telefono}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-muted-foreground">€ {r.premio_lordo_annuo}/anno</span>
                    <span className="rounded-full bg-white px-2 py-0.5 text-[10px] text-terracotta">{r.stato}</span>
                    {isAdmin && !r.quote_id && (
                      <Button size="sm" onClick={() => trasforma(r.id)} data-testid={`fs-trasforma-${r.id}`}
                        className="h-7 gap-1.5 rounded-full bg-forest text-xs hover:bg-forest/90">
                        <FileSignature className="h-3.5 w-3.5" /> Adesione a distanza (OTP)
                      </Button>
                    )}
                  </div>
                </div>
                {(adesioneLinks[r.id] || r.quote_id) && (
                  <div className="mt-2 flex items-center gap-2">
                    <Input readOnly value={adesioneLinks[r.id] || "Adesione già generata — apri il registro Adesioni"} className="h-8 text-xs" data-testid={`fs-adesione-url-${r.id}`} />
                    {adesioneLinks[r.id] && (
                      <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(adesioneLinks[r.id]); toast.success("Link copiato"); }} className="h-8 shrink-0 gap-1">
                        <Copy className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </Card>
      )}

      {mine.length > 0 && (
        <Card className="rounded-xl border-border/60 p-4" data-testid="fs-my-links">
          <p className="mb-3 text-sm font-semibold text-forest">I miei link ({mine.length})</p>
          <div className="space-y-1.5">
            {mine.slice(0, 8).map((q) => (
              <div key={q.short_id} className="flex items-center justify-between gap-2 rounded-lg bg-[#F0F4F1] px-3 py-2 text-xs">
                <span className="font-mono text-forest">/p/{q.short_id}</span>
                <span className="text-muted-foreground">{q.composition_type} · € {q.premio_lordo_annuo}/anno</span>
                <span className="rounded-full bg-white px-2 py-0.5 text-[10px] text-terracotta">{q.status}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
